import { useTranslation } from "react-i18next";
import ActionButton from "./ActionButton";
import AttendanceMatrix from "./AttendanceMatrix";
import Field from "./Field";
import MemberList from "./MemberList";
import TurnstileWidget from "../common/TurnstileWidget";

const TURNSTILE_SITE_KEY = (
  import.meta.env.VITE_TURNSTILE_SITE_KEY || ""
).trim();

// ─── Sub-renderers ─────────────────────────────────────────────

const IdentityStep = ({ flow, onBack }) => {
  const { t } = useTranslation();
  const {
    reqFirstName,
    reqLastName,
    reqEmail,
    reqPhone,
    reqExtraMembers,
    reqHoneypot,
    canProceedReqStep1,
    submitting,
    error,
    actions,
  } = flow;

  return (
    <div>
      <p
        id="rsvp-modal-title"
        className="heading-60"
        style={{ marginBottom: 10, textAlign: "center" }}
      >
        {t("rsvp.request.title")}
      </p>
      <p
        className="body-16"
        style={{ opacity: 0.8, marginBottom: 30, textAlign: "center" }}
      >
        {t("rsvp.request.subtitle")}
      </p>

      <div style={{ maxWidth: 480, margin: "0 auto" }}>
        <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
          <Field
            type="text"
            placeholder={t("rsvp.request.firstNamePlaceholder")}
            value={reqFirstName}
            onChange={(e) => actions.setReqFirstName(e.target.value)}
            disabled={submitting}
            style={{ flex: 1 }}
          />
          <Field
            type="text"
            placeholder={t("rsvp.request.lastNamePlaceholder")}
            value={reqLastName}
            onChange={(e) => actions.setReqLastName(e.target.value)}
            disabled={submitting}
            style={{ flex: 1 }}
          />
        </div>

        <Field
          type="email"
          placeholder={t("rsvp.request.emailPlaceholder")}
          value={reqEmail}
          onChange={(e) => actions.setReqEmail(e.target.value)}
          disabled={submitting}
          style={{ marginBottom: 12, width: "100%" }}
        />

        <Field
          type="tel"
          placeholder={t("rsvp.request.phonePlaceholder")}
          value={reqPhone}
          onChange={(e) => actions.setReqPhone(e.target.value)}
          disabled={submitting}
          style={{ marginBottom: 12, width: "100%" }}
        />

        <p
          className="body-16"
          style={{
            opacity: 0.6,
            fontSize: 13,
            textAlign: "center",
            marginBottom: 30,
          }}
        >
          {t("rsvp.request.contactNote")}
        </p>

        {/* Honeypot — hidden from humans. Must live inside the form
            subtree so naive form-fillers populate it. */}
        <input
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={reqHoneypot}
          onChange={(e) => actions.setReqHoneypot(e.target.value)}
          style={{
            position: "absolute",
            left: "-9999px",
            opacity: 0,
            pointerEvents: "none",
          }}
          aria-hidden="true"
        />

        <p
          className="body-16"
          style={{ fontWeight: 600, marginBottom: 12, textAlign: "center" }}
        >
          {t("rsvp.request.extraMembers")}
        </p>
        <MemberList
          items={reqExtraMembers}
          onAdd={actions.addReqExtraMember}
          onUpdate={actions.updateReqExtraMember}
          onRemove={actions.removeReqExtraMember}
          disabled={submitting}
        />
      </div>

      {error && (
        <p
          className="body-16"
          style={{
            color: "#a23b31",
            textAlign: "center",
            marginTop: 20,
          }}
        >
          {error}
        </p>
      )}

      <div
        style={{
          display: "flex",
          gap: 16,
          justifyContent: "center",
          flexWrap: "wrap",
          marginTop: 30,
        }}
      >
        <ActionButton variant="ghost" onClick={onBack} disabled={submitting}>
          {t("rsvp.buttons.back")}
        </ActionButton>
        <ActionButton
          variant="primary"
          onClick={() => actions.setStep(1)}
          disabled={!canProceedReqStep1 || submitting}
        >
          {t("rsvp.buttons.continue")}
        </ActionButton>
      </div>
    </div>
  );
};

const AttendanceStep = ({ flow }) => {
  const { t } = useTranslation();
  const {
    reqSections,
    reqAllMembers,
    reqResponses,
    submitting,
    actions,
    isReqAttendanceComplete,
    hasReqAttendanceStarted,
  } = flow;
  const showIncompleteHelper =
    reqSections.length > 0 &&
    !isReqAttendanceComplete &&
    hasReqAttendanceStarted;

  return (
    <div>
      <div style={{ textAlign: "center", marginBottom: 20 }}>
        <h2
          id="rsvp-modal-title"
          className="heading-60"
          style={{
            margin: 0,
            fontSize: 30,
            lineHeight: 1.2,
            marginBottom: 6,
          }}
        >
          {t("rsvp.attendance.title")}
        </h2>
        <p className="body-16" style={{ opacity: 0.6, margin: 0 }}>
          {t("rsvp.attendance.subtitle")}
        </p>
      </div>
      {reqSections.length === 0 ? (
        <p
          className="body-16"
          style={{ textAlign: "center", opacity: 0.7, marginBottom: 24 }}
        >
          {t("rsvp.request.loadingSections")}
        </p>
      ) : (
        <AttendanceMatrix
          sections={reqSections}
          members={reqAllMembers}
          responses={reqResponses}
          onChange={actions.setReqStatus}
          disabled={submitting}
        />
      )}
      {showIncompleteHelper && (
        <p
          className="body-16"
          style={{
            opacity: 0.6,
            textAlign: "center",
            marginTop: 14,
            marginBottom: 0,
            fontSize: 13,
          }}
        >
          {t("rsvp.attendance.validation")}
        </p>
      )}
      <div
        style={{
          display: "flex",
          gap: 16,
          justifyContent: "center",
          flexWrap: "wrap",
          marginTop: showIncompleteHelper ? 12 : 8,
        }}
      >
        <ActionButton
          variant="ghost"
          onClick={() => actions.setStep(0)}
          disabled={submitting}
        >
          {t("rsvp.buttons.back")}
        </ActionButton>
        <ActionButton
          variant="primary"
          onClick={() => actions.setStep(2)}
          disabled={
            reqSections.length === 0 || submitting || !isReqAttendanceComplete
          }
        >
          {t("rsvp.buttons.continue")}
        </ActionButton>
      </div>
    </div>
  );
};

const MessageStep = ({ flow }) => {
  const { t } = useTranslation();
  const { reqMessage, reqTurnstileToken, submitting, error, actions } = flow;
  return (
    <div>
      <p
        id="rsvp-modal-title"
        className="heading-60"
        style={{ marginBottom: 20, textAlign: "center" }}
      >
        {t("rsvp.message.title")}
      </p>
      <p
        className="body-16"
        style={{ opacity: 0.8, marginBottom: 30, textAlign: "center" }}
      >
        {t("rsvp.request.messageSubtitle")}
      </p>

      <div style={{ maxWidth: 520, margin: "0 auto 30px" }}>
        <Field
          as="textarea"
          placeholder={t("rsvp.message.placeholder")}
          value={reqMessage}
          onChange={(e) => actions.setReqMessage(e.target.value)}
          rows={4}
          maxLength={2000}
          disabled={submitting}
          style={{
            width: "100%",
            resize: "vertical",
            minHeight: 100,
            fontFamily: "inherit",
          }}
        />
      </div>

      {error && (
        <p
          className="body-16"
          style={{
            color: "#a23b31",
            textAlign: "center",
            marginBottom: 20,
          }}
        >
          {error}
        </p>
      )}

      {TURNSTILE_SITE_KEY && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginBottom: 16,
          }}
        >
          <TurnstileWidget
            siteKey={TURNSTILE_SITE_KEY}
            onToken={actions.setReqTurnstileToken}
          />
        </div>
      )}

      <div
        style={{
          display: "flex",
          gap: 16,
          justifyContent: "center",
          flexWrap: "wrap",
          marginTop: 20,
        }}
      >
        <ActionButton
          variant="ghost"
          onClick={() => actions.setStep(1)}
          disabled={submitting}
        >
          {t("rsvp.buttons.back")}
        </ActionButton>
        <ActionButton
          variant="primary"
          onClick={actions.submitRequest}
          disabled={
            submitting || (!!TURNSTILE_SITE_KEY && !reqTurnstileToken)
          }
        >
          {submitting ? t("rsvp.buttons.sending") : t("rsvp.buttons.sendRequest")}
        </ActionButton>
      </div>
    </div>
  );
};

const Done = ({ onClose, isDemo }) => {
  const { t } = useTranslation();
  // Demo mode: the BE never persisted this submission. Show preview-only
  // copy and swap the return-link for a "Create your own" CTA pointing at
  // the app's create-event flow — same env var as the rest of invite/'s
  // cross-app links (see RSVP.jsx). Force Heirloom plan since the demo
  // showcases the full premium experience (matches DemoRibbon CTA).
  const createOwnUrl = `${import.meta.env.VITE_APP_URL || ""}/couple/create-event?plan=heirloom`;
  return (
    <div style={{ textAlign: "center", padding: "24px 0 16px" }}>
      {/* Subtle hairline flourish — signals arrival at the end of the flow */}
      <div
        aria-hidden="true"
        style={{
          width: 48,
          height: 1,
          background: "rgba(0,0,0,0.25)",
          margin: "0 auto 28px",
        }}
      />
      <h2
        id="rsvp-modal-title"
        className="heading-60"
        style={{ margin: 0, marginBottom: 14 }}
      >
        {isDemo ? t("rsvp.request.demoThankYou") : t("rsvp.request.thankYou")}
      </h2>
      <p
        className="body-16"
        style={{
          opacity: 0.65,
          margin: 0,
          marginBottom: 36,
          maxWidth: 420,
          marginLeft: "auto",
          marginRight: "auto",
        }}
      >
        {isDemo ? t("rsvp.request.demoNextSteps") : t("rsvp.request.nextSteps")}
      </p>
      {isDemo ? (
        <a
          href={createOwnUrl}
          className="body-16"
          style={{
            display: "inline-block",
            background: "var(--black)",
            color: "var(--white, #fff)",
            padding: "12px 24px",
            borderRadius: 999,
            textDecoration: "none",
            fontSize: 14,
            letterSpacing: "0.02em",
          }}
        >
          {t("rsvp.request.demoCreateOwn")}
        </a>
      ) : (
        <button
          type="button"
          onClick={onClose}
          className="body-16"
          style={{
            background: "none",
            border: "none",
            color: "var(--black)",
            opacity: 0.7,
            cursor: "pointer",
            padding: 0,
            fontSize: 14,
            textDecoration: "underline",
            textUnderlineOffset: 3,
          }}
        >
          {t("rsvp.request.returnButton")}
        </button>
      )}
    </div>
  );
};

// ─── Main component ────────────────────────────────────────────

/**
 * RequestFlow
 *
 * Orchestrates the request-to-attend experience: step 0 identity,
 * step 1 attendance matrix, step 2 optional message, then a done
 * confirmation screen after submission succeeds.
 *
 * Props:
 *   flow    — the useRsvpFlow return value
 *   onBack  — switch back to ChoiceScreen (step 0 only)
 *   onClose — used on the Done screen (tells the parent to close the modal)
 */
const RequestFlow = ({ flow, onBack, onClose }) => {
  if (flow.reqSubmitted)
    return <Done onClose={onClose} isDemo={flow.reqDemoSubmission} />;
  switch (flow.step) {
    case 0:
      return <IdentityStep flow={flow} onBack={onBack} />;
    case 1:
      return <AttendanceStep flow={flow} />;
    case 2:
      return <MessageStep flow={flow} />;
    default:
      return null;
  }
};

export default RequestFlow;
